import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Crown } from "lucide-react";
import { FadeUp, Eyebrow } from "./Reveal";
import { stripePromise } from "@/lib/stripe";
import { detectCurrency } from "@/lib/currency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scrollTo = (href) => {
  if (window.__lenis) window.__lenis.scrollTo(href, { offset: -72, duration: 1.4 });
  else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

export const PRICE_DISPLAY = {
  monthly: { NZD: "$12", USD: "$7.99", AUD: "$12.99", GBP: "£5.99", EUR: "€6.99" },
  annual: { NZD: "$89", USD: "$59", AUD: "$99", GBP: "£49", EUR: "€55" },
  lifetime: { NZD: "$199", USD: "$129", AUD: "$219", GBP: "£99", EUR: "€119" },
};

const PLANS = [
  {
    id: "monthly",
    name: "Monthly",
    price: "$12",
    per: "/month",
    tagline: "Full access, cancel anytime.",
    features: [
      "All five durations: 10 / 20 / 30 / 40 / 50 min",
      "Adaptive session engine + circuit training mode",
      "500+ exercise library with male & female demos",
      "Live heart-rate zone coaching",
      "High-protein meal recipe library",
      "Streaks & minute tracking",
    ],
    cta: "Start Monthly",
    featured: false,
  },
  {
    id: "annual",
    name: "Founding Annual",
    price: "$89",
    per: "/year",
    tagline: "Founding rate locked for life.",
    badge: "Most Popular — Save 38%",
    features: [
      "Everything in Monthly",
      "Time-bracket leaderboards",
      "Offline session downloads",
      "Priority coach support",
      "Rate never increases for founders",
    ],
    cta: "Lock Founding Rate",
    featured: true,
  },
  {
    id: "lifetime",
    name: "Founding Lifetime",
    price: "$199",
    per: " once",
    tagline: "Pay once. Train forever.",
    badge: "First 500 Members Only",
    features: [
      "Everything in Annual, forever",
      "Founding member badge in-app",
      "Vote on new workout drops + recipe drops",
      "Early access to every feature",
    ],
    cta: "Claim Lifetime",
    featured: false,
  },
];

export const Pricing = () => {
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [currency, setCurrency] = useState("NZD");
  const [founding, setFounding] = useState(null);

  useEffect(() => {
    detectCurrency().then(setCurrency);
    const load = () =>
      axios
        .get(`${API}/stats/founding`)
        .then((res) => setFounding(res.data.total))
        .catch(() => setFounding((prev) => prev ?? 47));
    load();
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, []);

  const checkout = async (plan) => {
    setLoadingPlan(plan);
    try {
      await stripePromise;
      const res = await axios.post(`${API}/payments/checkout`, {
        plan,
        origin_url: window.location.origin,
        currency,
      });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Couldn't start checkout. Try again.");
      setLoadingPlan(null);
    }
  };

  return (
  <section id="pricing" data-testid="pricing-section" className="relative py-32 scroll-mt-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <FadeUp className="text-center">
        <Eyebrow>Founding Member Pricing</Eyebrow>
        <h2 className="font-display font-extrabold uppercase tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl mt-4">
          Cheaper than <span className="text-volt">one skipped gym month</span>
        </h2>
        <p className="mt-5 max-w-xl mx-auto text-base sm:text-lg text-neutral-400 leading-relaxed">
          Launch pricing for the first 500 founding members. Every plan starts with a free
          10-Min Ignition session — no card required.
        </p>
        <div data-testid="founding-counter" className="mt-8 mx-auto max-w-md">
          <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-volt">Founding spots claimed</p>
          <div className="mt-3 flex justify-between font-mono text-xs tracking-widest uppercase">
            <span className="text-neutral-400">
              {founding === null ? "…" : founding} of 500 spots taken
            </span>
            <span className="text-volt">{founding === null ? "" : `${Math.max(500 - founding, 0)} left`}</span>
          </div>
          <div data-testid="founding-progress-bar" className="mt-2 h-2 bg-white/10 overflow-hidden">
            <div
              className="h-full bg-volt transition-all duration-1000"
              style={{ width: `${Math.min(((founding ?? 0) / 500) * 100, 100)}%` }}
            />
          </div>
          <p className="mt-3 font-mono text-[10px] tracking-widest uppercase text-neutral-500">
            Price increases permanently when all 500 are gone.
          </p>
        </div>
      </FadeUp>

      <div className="mt-16 grid md:grid-cols-3 gap-4 items-stretch">
        {PLANS.map((p, i) => (
          <FadeUp key={p.id} delay={i * 0.1} className="h-full">
            <div
              data-testid={`pricing-card-${p.id}`}
              className={`relative h-full flex flex-col p-8 border transition-[border-color,transform] duration-300 hover:-translate-y-1 ${
                p.featured
                  ? "border-volt bg-carbon shadow-[0_0_50px_rgba(255,107,0,0.12)]"
                  : "border-white/10 bg-carbon hover:border-volt/40"
              }`}
            >
              {p.badge && (
                <span className={`absolute -top-3 left-6 px-3 py-1 font-mono text-[10px] tracking-widest uppercase ${
                  p.featured ? "bg-volt text-white font-semibold" : "bg-obsidian border border-volt/50 text-volt"
                }`}>
                  {p.badge}
                </span>
              )}
              <div className="flex items-center gap-2">
                {p.id === "lifetime" && <Crown className="h-5 w-5 text-volt" />}
                <h3 className="font-display font-bold uppercase text-lg tracking-wide text-neutral-300">{p.name}</h3>
              </div>
              <div className="mt-5 flex items-baseline gap-1">
                <span className={`font-display font-black text-6xl ${p.featured ? "text-volt" : ""}`}>{PRICE_DISPLAY[p.id][currency]}</span>
                <span className="font-mono text-xs tracking-widest uppercase text-neutral-500">{p.per} {currency}</span>
              </div>
              <p className="mt-2 text-sm text-neutral-500">{p.tagline}</p>
              <ul className="mt-7 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-neutral-300">
                    <Check className="h-4 w-4 text-volt shrink-0 mt-0.5" strokeWidth={3} />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`pricing-select-${p.id}-button`}
                onClick={() => checkout(p.id)}
                disabled={loadingPlan !== null}
                className={`mt-8 w-full py-4 font-display font-bold uppercase tracking-wide transition-colors duration-200 disabled:opacity-60 ${
                  p.featured
                    ? "bg-volt text-white hover:bg-volt-hover"
                    : "border border-white/25 text-neutral-100 hover:border-volt hover:text-volt"
                }`}
              >
                {loadingPlan === p.id ? "Redirecting..." : p.cta}
              </button>
              <button
                data-testid={`pricing-waitlist-${p.id}-link`}
                onClick={() => scrollTo("#access")}
                className="mt-3 w-full text-center font-mono text-[11px] tracking-widest uppercase text-neutral-500 hover:text-volt transition-colors duration-200"
              >
                Join waitlist instead
              </button>
            </div>
          </FadeUp>
        ))}
      </div>

      <FadeUp delay={0.3}>
        <p className="mt-8 text-center font-mono text-[11px] tracking-widest uppercase text-neutral-600">
          Founding prices disappear when the waitlist hits 500 members
        </p>
      </FadeUp>
    </div>
  </section>
  );
};
